import {
  EnvType,
  LimitedPrivateAPI,
  PlatformPrivateServices,
} from '@wix/editor-platform-private-api';
import {
  IPlatformPrivateEvent,
  PlatformPrivateEventEmitter,
} from '@wix/public-editor-platform-events';
import { ApplicationType } from '@wix/editor-application';
import {
  createEditorPlatformWorkerError,
  EditorPlatformWorkerErrorCode,
} from '../../errors';
import {
  WithPromiseFunctions,
  IFrameHostEndpoint,
} from '@wix/editor-platform-transport';
import {
  PlatformFrameAPI,
  PlatformConsumerEnvironmentAPIType,
} from '@wix/editor-platform-environment-api';

export class PlatformFrameStrategy {
  #connectedIframes = new Map<
    HTMLIFrameElement,
    WithPromiseFunctions<PlatformFrameAPI>
  >();

  #pendingIframes = new Set<HTMLIFrameElement>();

  constructor(
    private ps: PlatformPrivateServices,
    private limitedPrivateAPI: LimitedPrivateAPI,
    private events: PlatformPrivateEventEmitter,
  ) {}

  /**
   * Cleanup closed iframes.
   * Closed iframes do not have content windows on them
   */
  #cleanup() {
    Array.from(this.#connectedIframes.keys()).forEach((iframe) => {
      if (!iframe.contentWindow) {
        this.#connectedIframes.delete(iframe);
      }
    });

    Array.from(this.#pendingIframes.values()).forEach((iframe) => {
      if (!iframe.contentWindow) {
        this.#pendingIframes.delete(iframe);
      }
    });
  }

  notify(event: IPlatformPrivateEvent) {
    this.#cleanup();

    Array.from(this.#connectedIframes.values()).forEach((frameAPI) => {
      void frameAPI.notify(event);
    });
  }

  connect(appDefinitionId: string, iframe: HTMLIFrameElement) {
    /**
     * the guard to resolve cases when for some reason
     * React ref is called multiple times which leads
     * to connect the same iframe to EP infra multiple times
     */
    if (this.#pendingIframes.has(iframe)) {
      return;
    }

    this.#pendingIframes.add(iframe);

    const frameInitEvent = this.events.factories.createIframeInitEvent(
      iframe.src,
      iframe.id || iframe.src,
    );

    return this.events.withEvent(frameInitEvent, async () => {
      if (!iframe.contentWindow) {
        throw createEditorPlatformWorkerError(
          EditorPlatformWorkerErrorCode.FrameInitError,
          'frame "contentWindow" is not available',
        );
      }

      let applicationPrivateAPI;

      /**
       * to discuss –
       * currently we have cases when we run frame for CE,
       * but there is no running application with the needed appDefinitionId
       * by design we throw exception if call any operation over application
       * which does not exist.
       *
       * this is temporary try/catch
       *
       * as soon as we run CE app, we remove this statement.
       */
      try {
        applicationPrivateAPI = await this.ps.apps.getPrivateAPI(
          appDefinitionId,
        );
      } catch (e) {
        applicationPrivateAPI = {};
      }

      IFrameHostEndpoint.connect(iframe, async (transport) => {
        const frameAPI = await transport.waitAPI<PlatformFrameAPI>(
          PlatformConsumerEnvironmentAPIType.Frame,
        );

        this.#connectedIframes.set(iframe, frameAPI);

        const limitedPrivateAPI = this.limitedPrivateAPI.build({
          type: ApplicationType.EditorAddon,
          env: EnvType.Frame,
        });

        /**
         * not connect iframe panels to manifest-related SDK,
         * we need to get the editor pointer here to build
         * the virtual manifest
         */
        const manifestContextId = null;
        // const manifestContextId = this.ps.manifestsManager.buildContextualManifest({});

        await frameAPI.initEnvironment({
          appDefinitionId,
          context: {
            manifestContextId,
            appDefinitionId,
          },
          privateAPI: limitedPrivateAPI,
          applicationPrivateAPI,
        });
      });
    });
  }
}
